import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { Users, Award, Settings, Clock, Heart, Building2 } from "lucide-react";
import { BENEFITS } from "@/lib/content";
import Tilt from "@/components/site/Tilt";

const ACCENTS = [
  { icon: Users, bg: "bg-sky-100", text: "text-sky-700", border: "border-sky-200", ring: "ring-sky-200/60", glow: "shadow-sky-200/50" },
  { icon: Award, bg: "bg-amber-100", text: "text-amber-700", border: "border-amber-200", ring: "ring-amber-200/60", glow: "shadow-amber-200/50" },
  { icon: Settings, bg: "bg-violet-100", text: "text-violet-700", border: "border-violet-200", ring: "ring-violet-200/60", glow: "shadow-violet-200/50" },
  { icon: Clock, bg: "bg-emerald-100", text: "text-emerald-700", border: "border-emerald-200", ring: "ring-emerald-200/60", glow: "shadow-emerald-200/50" },
  { icon: Heart, bg: "bg-rose-100", text: "text-rose-700", border: "border-rose-200", ring: "ring-rose-200/60", glow: "shadow-rose-200/50" },
  { icon: Building2, bg: "bg-teal-100", text: "text-teal-700", border: "border-teal-200", ring: "ring-teal-200/60", glow: "shadow-teal-200/50" },
];

const HERO_IMAGE = "https://images.pexels.com/photos/4099467/pexels-photo-4099467.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1200&w=1600";

export default function WhyUs() {
  const sectionRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start end", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], ["-10%", "10%"]);
  const blob1Y = useTransform(scrollYProgress, [0, 1], ["0%", "-30%"]);
  const blob2Y = useTransform(scrollYProgress, [0, 1], ["0%", "25%"]);

  return (
    <section
      id="warum"
      data-testid="whyus-section"
      ref={sectionRef}
      className="relative py-24 md:py-32 overflow-hidden bg-gradient-to-b from-white via-sky-50/50 to-amber-50/40"
      style={{ perspective: "1200px" }}
    >
      {/* Animated decorative blobs */}
      <motion.div
        style={{ y: blob1Y }}
        className="pointer-events-none absolute -top-20 -right-32 w-[520px] h-[520px] rounded-full bg-gradient-to-br from-sky-300/40 via-sky-400/20 to-transparent blur-3xl"
      />
      <motion.div
        style={{ y: blob2Y }}
        className="pointer-events-none absolute top-1/3 -left-32 w-[420px] h-[420px] rounded-full bg-gradient-to-br from-rose-300/30 via-amber-300/20 to-transparent blur-3xl"
      />
      <motion.div
        style={{ y: blob1Y }}
        className="pointer-events-none absolute bottom-0 right-1/4 w-[360px] h-[360px] rounded-full bg-gradient-to-br from-emerald-300/25 to-transparent blur-3xl"
      />

      <div className="relative mx-auto max-w-7xl px-6 md:px-10 lg:px-12">
        <div className="max-w-2xl mb-14 md:mb-20">
          <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-sky-700">
            <span className="w-6 h-px bg-sky-600" /> Warum Kola
          </span>
          <h2 className="font-display mt-4 text-4xl sm:text-5xl font-semibold text-slate-900 leading-[1.05]">
            Vertrauen durch{" "}
            <span className="relative inline-block">
              <span className="relative z-10 bg-gradient-to-r from-sky-600 via-sky-500 to-emerald-500 bg-clip-text text-transparent">
                konstante Qualität
              </span>
              <span className="absolute left-0 right-0 bottom-1 h-3 bg-amber-200/60 -z-0 rounded-sm" />
            </span>
            .
          </h2>
          <p className="mt-5 text-slate-600 text-base md:text-lg leading-relaxed">
            Als familiengeführter Betrieb stehen wir persönlich für unsere Arbeit ein —
            jeden Tag, bei jedem Einsatz.
          </p>
        </div>

        {/* Bento grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
          {/* Large image card */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.6 }}
            className="md:row-span-2 md:col-span-1 relative overflow-hidden rounded-3xl min-h-[420px] shadow-[0_30px_80px_-25px_rgba(2,132,199,0.45)] ring-1 ring-white"
          >
            <motion.img
              style={{ y: imgY }}
              src={HERO_IMAGE}
              alt="Kola Reinigungsteam — familiengeführt"
              className="absolute -inset-10 w-[calc(100%+5rem)] h-[calc(100%+5rem)] object-cover"
            />
            {/* Colorful gradient overlay */}
            <div className="absolute inset-0 bg-gradient-to-br from-sky-700/60 via-sky-900/45 to-rose-900/40" />
            <div className="absolute inset-0 bg-gradient-to-t from-slate-950/70 via-transparent to-transparent" />

            {/* Badge top-right */}
            <div className="absolute top-5 right-5 inline-flex items-center gap-2 rounded-full bg-white/90 backdrop-blur px-3.5 py-1.5 text-xs font-semibold text-slate-900 shadow-lg">
              <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
              Familiengeführt seit Jahren
            </div>

            <div className="relative h-full flex flex-col justify-end p-8 text-white">
              <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-sky-200">
                <Heart className="w-3.5 h-3.5 fill-rose-400 text-rose-400" /> Unser Versprechen
              </div>
              <h3 className="font-display text-3xl md:text-4xl font-semibold mt-2 leading-tight">
                Unser Team.<br />
                <span className="bg-gradient-to-r from-sky-200 via-white to-amber-200 bg-clip-text text-transparent">
                  Ihr Vertrauen.
                </span>
              </h3>
              <p className="mt-4 text-sm text-white/85 max-w-xs leading-relaxed">
                Feste Ansprechpartner, persönlich erreichbar — vom Erstgespräch bis
                zur laufenden Reinigung.
              </p>

              {/* Avatars cluster */}
              <div className="mt-6 flex items-center gap-3">
                <div className="flex -space-x-2">
                  {["bg-amber-300", "bg-sky-300", "bg-rose-300", "bg-emerald-300"].map((c, i) => (
                    <span key={i} className={`w-8 h-8 rounded-full ${c} ring-2 ring-white shadow-md`} />
                  ))}
                </div>
                <span className="text-xs text-white/80">100+ zufriedene Objekte</span>
              </div>
            </div>
          </motion.div>

          {BENEFITS.map((b, i) => {
            const a = ACCENTS[i % ACCENTS.length];
            const Icon = a.icon;
            return (
              <motion.div
                key={b.t}
                data-testid={`benefit-${i}`}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.5, delay: 0.06 * i }}
              >
                <Tilt max={7} scale={1.015}>
                  <div
                    className={`group relative rounded-2xl bg-white border ${a.border} p-7 shadow-[0_10px_30px_-12px_rgba(15,23,42,0.08)] hover:shadow-[0_25px_60px_-20px_rgba(15,23,42,0.15)] transition-shadow duration-500 overflow-hidden h-full`}
                  >
                    {/* corner color wash */}
                    <div
                      className={`absolute -top-16 -right-16 w-48 h-48 rounded-full ${a.bg} opacity-60 blur-2xl transition-all duration-500 group-hover:opacity-90 group-hover:scale-110`}
                    />
                    <div className="relative flex items-start gap-4">
                      <span
                        className={`grid place-items-center w-12 h-12 rounded-2xl ${a.bg} ${a.text} border ${a.border} shrink-0 shadow-[0_8px_20px_-8px] ${a.glow} ring-4 ${a.ring} transition-transform group-hover:rotate-3 group-hover:scale-105`}
                        style={{ transform: "translateZ(30px)" }}
                      >
                        <Icon className="w-5 h-5" strokeWidth={2.2} />
                      </span>
                      <div style={{ transform: "translateZ(15px)" }}>
                        <h3 className="font-display text-lg font-semibold text-slate-900">
                          {b.t}
                        </h3>
                        <p className="mt-1.5 text-sm text-slate-600 leading-relaxed">
                          {b.d}
                        </p>
                      </div>
                    </div>
                  </div>
                </Tilt>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
